import React, { useMemo } from "react";
import { Flex, useTranslation, Thead, Tr, Th, DraggableTable, DraggableTbody, arrayMove } from "@familyzone/component-library";

import { FirewallRule } from "./FirewallRule";

import FirewallPolicyTableRow from "./FirewallPolicyTableRow";
import NoContentDisplay from "../../pages/filtering/NoContentDisplay";

interface FilteringTableProps {
  loading?: boolean;
  disabled?: boolean;
  rules: FirewallRule[];
  onSortRules: (rules: FirewallRule[]) => void;
  onEditRule: (rule: FirewallRule) => void;
  onDeleteRule: (rule: FirewallRule) => void;
  onToggleRule: (rule: FirewallRule) => void;
}

const FilteringTable: React.FC<FilteringTableProps> = ({
  loading = false,
  disabled = false,
  rules = [],
  onSortRules = () => "",
  onEditRule = () => "",
  onDeleteRule = () => "",
  onToggleRule = () => "",
}) => {
  const { t } = useTranslation();

  const columns: string[] = ["", t("Enable"), t("Action"), t("Policy Name"), t("Source"), t("Destination"), t("Operations")];

  const noContentTitle = t("Create your first policy");
  const noContentText = t("Create your first firewall policy by clicking on the “Create Policy” button");

  const sortedRules: FirewallRule[] = useMemo(() => {
    return [...rules];
  }, [rules]);

  if (rules.length === 0 && !loading) {
    return <NoContentDisplay title={noContentTitle} text={noContentText} />;
  }

  const handleMoveRule = (active: string | number, over: string | number) => {
    if (loading) return;
    if (active === over) return;

    const activeIndex = sortedRules.findIndex((r) => r.id === active);
    const overIndex = sortedRules.findIndex((r) => r.id === over);
    if (activeIndex === -1 || overIndex === -1) return;

    const newRules = arrayMove(sortedRules, activeIndex, overIndex);

    onSortRules(newRules);
  };

  return (
    <Flex mt="sp24">
      <DraggableTable onMove={handleMoveRule}>
        <Thead>
          <Tr>
            {columns.map((column, index) => (
              <Th
                key={`${index}-${column}`}
                headerText={column}
                columnName={column}
                alignment={index === columns.length - 1 ? "right" : "left"}
              />
            ))}
          </Tr>
        </Thead>
        <DraggableTbody items={sortedRules.map((r) => r.id)} loaded={!loading}>
          {sortedRules.map((rule) => (
            <FirewallPolicyTableRow
              draggable
              key={rule.id}
              rule={rule}
              id={rule.id}
              onToggleEnabled={() => onToggleRule(rule)}
              onEdit={() => onEditRule(rule)}
              onDelete={() => onDeleteRule(rule)}
              disabled={disabled}
            />
          ))}
        </DraggableTbody>
      </DraggableTable>
    </Flex>
  );
};

export default FilteringTable;
